import { useState, useEffect, useCallback } from "react";
import { useNavigate, Routes, Route, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Shield,
  Users,
  MessageSquare,
  Settings,
  LogOut,
  BarChart3,
  Plug,
  Trash2,
  Ban,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Check,
  X,
  ExternalLink,
  RefreshCw,
  Link as LinkIcon,
  Code,
  Copy,
  KeyRound,
  Info,
  Plus,
  Pencil,
  Key,
  Globe,
  Link2,
  Webhook,
  Activity,
  Bot,
  Server,
  Phone,
} from "lucide-react";
import { API } from "../App";
import SuperAdminConnect from "./SuperAdminConnect";
import AIAgentsManager from "./AIAgentsManager";
import APIKeysManager from "./APIKeysManager";
import KapsoServerPanel from "../components/KapsoServerPanel";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Helper to get admin auth headers
  const getAdminHeaders = () => {
    const token = localStorage.getItem('admin_session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Check admin session
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetch(`${API}/admin/me`, {
          credentials: "include",
          headers: getAdminHeaders(),
        });
        
        if (response.ok) {
          const data = await response.json();
          setAdmin(data);
        } else {
          localStorage.removeItem('admin_session_token');
          navigate("/administrador", { replace: true });
        }
      } catch (error) {
        navigate("/administrador", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/admin/logout`, {
        method: "POST",
        credentials: "include",
        headers: getAdminHeaders(),
      });
      localStorage.removeItem('admin_session_token');
      toast.success("Sesión cerrada");
      navigate("/administrador", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-400">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar */}
      <div className="w-64 admin-sidebar flex flex-col">
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                OkChat
              </span>
              <p className="text-xs text-slate-400">Admin Panel</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <button
            onClick={() => setActiveTab("overview")}
            className={`admin-sidebar-item w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left ${
              activeTab === "overview" ? "active text-white" : "text-slate-400 hover:text-white"
            }`}
            data-testid="nav-overview"
          >
            <BarChart3 className="w-5 h-5" />
            <span>Resumen</span>
          </button>
          <button
            onClick={() => setActiveTab("users")}
            className={`admin-sidebar-item w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left ${
              activeTab === "users" ? "active text-white" : "text-slate-400 hover:text-white"
            }`}
            data-testid="nav-users"
          >
            <Users className="w-5 h-5" />
            <span>Usuarios</span>
          </button>
          <button
            onClick={() => setActiveTab("integrations")}
            className={`admin-sidebar-item w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left ${
              activeTab === "integrations" ? "active text-white" : "text-slate-400 hover:text-white"
            }`}
            data-testid="nav-integrations"
          >
            <Plug className="w-5 h-5" />
            <span>Integraciones</span>
          </button>
          <button
            onClick={() => setActiveTab("embed")}
            className={`admin-sidebar-item w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left ${
              activeTab === "embed" ? "active text-white" : "text-slate-400 hover:text-white"
            }`}
            data-testid="nav-embed"
          >
            <Code className="w-5 h-5" />
            <span>Embed / Widget</span>
          </button>
          <button
            onClick={() => setActiveTab("oauth-sso")}
            className={`admin-sidebar-item w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left ${
              activeTab === "oauth-sso" ? "active text-white" : "text-slate-400 hover:text-white"
            }`}
            data-testid="nav-oauth-sso"
          >
            <KeyRound className="w-5 h-5" />
            <span>OAuth SSO</span>
          </button>
          <button
            onClick={() => setActiveTab("super-admin-connect")}
            className={`admin-sidebar-item w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left ${
              activeTab === "super-admin-connect" ? "active text-white" : "text-slate-400 hover:text-white"
            }`}
            data-testid="nav-super-admin-connect"
          >
            <Link2 className="w-5 h-5" />
            <span>Super Admin Connect</span>
          </button>
          <button
            onClick={() => setActiveTab("ai-agents")}
            className={`admin-sidebar-item w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left ${
              activeTab === "ai-agents" ? "active text-white" : "text-slate-400 hover:text-white"
            }`}
            data-testid="nav-ai-agents"
          >
            <Bot className="w-5 h-5" />
            <span>Agentes IA</span>
          </button>
          <button
            onClick={() => setActiveTab("api-connection")}
            className={`admin-sidebar-item w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left ${
              activeTab === "api-connection" ? "active text-white" : "text-slate-400 hover:text-white"
            }`}
            data-testid="nav-api-connection"
          >
            <Server className="w-5 h-5" />
            <span>Conexión API</span>
          </button>
          <button
            onClick={() => setActiveTab("kapso")}
            className={`admin-sidebar-item w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left ${
              activeTab === "kapso" ? "active text-white" : "text-slate-400 hover:text-white"
            }`}
            data-testid="nav-kapso"
          >
            <Phone className="w-5 h-5" />
            <span>Kapso Server</span>
          </button>
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="flex items-center space-x-3 mb-4">
            <Avatar className="w-10 h-10">
              <AvatarFallback className="bg-red-600 text-white">
                {admin?.name?.charAt(0) || "A"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{admin?.name}</p>
              <p className="text-xs text-slate-400 truncate">{admin?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          {activeTab === "overview" && <OverviewTab />}
          {activeTab === "users" && <UsersTab />}
          {activeTab === "integrations" && <IntegrationsTab />}
          {activeTab === "embed" && <EmbedTab />}
          {activeTab === "oauth-sso" && <OAuthSSOTab />}
          {activeTab === "super-admin-connect" && <SuperAdminConnect getAdminHeaders={getAdminHeaders} />}
          {activeTab === "ai-agents" && <AIAgentsManager getAdminHeaders={getAdminHeaders} />}
          {activeTab === "api-connection" && <APIConnectionTab getAdminHeaders={getAdminHeaders} />}
          {activeTab === "kapso" && <KapsoServerPanel getAdminHeaders={getAdminHeaders} />}
        </div>
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API}/admin/stats`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  const statCards = [
    { label: "Usuarios Totales", value: stats?.total_users || 0, icon: Users, color: "bg-red-600" },
    { label: "Usuarios En Línea", value: stats?.online_users || 0, icon: Users, color: "bg-emerald-500" },
    { label: "Conversaciones", value: stats?.total_conversations || 0, icon: MessageSquare, color: "bg-blue-500" },
    { label: "Mensajes Totales", value: stats?.total_messages || 0, icon: MessageSquare, color: "bg-orange-500" },
  ];

  return (
    <div>
      <h1 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Resumen General
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Card key={index} className="bg-slate-800 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">{stat.label}</p>
                  <p className="text-3xl font-bold text-white mt-1">{stat.value.toLocaleString()}</p>
                </div>
                <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Actividad de Hoy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-10 h-10 text-red-400" />
            </div>
            <p className="text-4xl font-bold text-white">{stats?.messages_today || 0}</p>
            <p className="text-slate-400 mt-2">Mensajes enviados hoy</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Users Tab
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creating, setCreating] = useState(false);
  const [changingPin, setChangingPin] = useState(false);
  
  // Create user form
  const [newUser, setNewUser] = useState({
    name: "",
    nickname: "",
    email: "",
    user_category: "personal"
  });
  
  // Change PIN form
  const [newPin, setNewPin] = useState("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/users?page=${page}&limit=10`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotalPages(data.pages);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.nickname) {
      toast.error("Nombre y nickname son requeridos");
      return;
    }
    
    setCreating(true);
    try {
      const response = await fetch(`${API}/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(newUser),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Usuario creado. PIN inicial: ${data.initial_pin}`);
        setShowCreateModal(false);
        setNewUser({ name: "", nickname: "", email: "", user_category: "personal" });
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al crear usuario");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setCreating(false);
    }
  };

  const handleChangePin = async () => {
    if (!newPin || newPin.length !== 4 || !newPin.match(/^\d{4}$/)) {
      toast.error("El PIN debe ser de 4 dígitos");
      return;
    }
    
    setChangingPin(true);
    try {
      const response = await fetch(`${API}/admin/users/${selectedUser.user_id}/pin`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ new_pin: newPin }),
      });
      
      if (response.ok) {
        toast.success("PIN actualizado");
        setShowPinModal(false);
        setNewPin("");
        setSelectedUser(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al cambiar PIN");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setChangingPin(false);
    }
  };

  const handleResetPin = async (userId, userName) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/reset-pin`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success(`PIN de ${userName} reseteado a 9999`);
        fetchUsers();
      } else {
        toast.error("Error al resetear PIN");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const handleChangeCategory = async (userId, newCategory) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/category?category=${newCategory}`, {
        method: "PUT",
        credentials: "include",
      });
      
      if (response.ok) {
        toast.success("Categoría actualizada");
        fetchUsers();
      } else {
        toast.error("Error al cambiar categoría");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Usuario eliminado");
        fetchUsers();
      } else {
        toast.error("Error al eliminar usuario");
      }
    } catch (error) {
      toast.error("Error al eliminar usuario");
    }
  };

  const handleBanUser = async (userId, reason = "") => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/ban`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: reason || "Violación de términos de servicio" }),
      });
      if (response.ok) {
        toast.success("Usuario baneado");
        fetchUsers();
      } else {
        toast.error("Error al banear usuario");
      }
    } catch (error) {
      toast.error("Error al banear usuario");
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/unban`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Usuario desbaneado");
        fetchUsers();
      } else {
        toast.error("Error al desbanear usuario");
      }
    } catch (error) {
      toast.error("Error al desbanear usuario");
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.nickname?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryBadge = (category) => {
    const badges = {
      personal: { label: "Usuario", color: "bg-slate-600 text-slate-200" },
      business: { label: "Negocio", color: "bg-green-600/20 text-green-400" },
      professional: { label: "Profesional", color: "bg-purple-600/20 text-purple-400" }
    };
    const badge = badges[category] || badges.personal;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Gestión de Usuarios
        </h1>
        <div className="flex items-center gap-4">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar usuarios..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="search-users-input"
            />
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="bg-red-600 hover:bg-red-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Crear Usuario
          </Button>
        </div>
      </div>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-red-500" />
              Crear Nuevo Usuario
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              El usuario recibirá el PIN inicial <strong className="text-white">9999</strong> y deberá cambiarlo al iniciar sesión.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Nombre Completo *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white mt-1"
                placeholder="Juan Pérez"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Nickname *</Label>
              <Input
                value={newUser.nickname}
                onChange={(e) => setNewUser({...newUser, nickname: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')})}
                className="bg-slate-900 border-slate-700 text-white mt-1"
                placeholder="juanperez"
              />
              <p className="text-xs text-slate-500 mt-1">Solo letras minúsculas, números y guiones bajos</p>
            </div>
            
            <div>
              <Label className="text-slate-300">Correo Electrónico (opcional)</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white mt-1"
                placeholder="juan@ejemplo.com"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Categoría</Label>
              <Select
                value={newUser.user_category}
                onValueChange={(value) => setNewUser({...newUser, user_category: value})}
              >
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                  <SelectValue placeholder="Seleccionar categoría" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="personal" className="text-white hover:bg-slate-700">Usuario (Personal)</SelectItem>
                  <SelectItem value="business" className="text-white hover:bg-slate-700">Negocio</SelectItem>
                  <SelectItem value="professional" className="text-white hover:bg-slate-700">Profesional</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-sm text-blue-300">
                <strong>PIN Inicial:</strong> 9999<br />
                El usuario deberá configurar su propio PIN al iniciar sesión por primera vez.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-slate-600 text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleCreateUser} disabled={creating} className="bg-red-600 hover:bg-red-700">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear Usuario"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change PIN Modal */}
      <Dialog open={showPinModal} onOpenChange={setShowPinModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-yellow-500" />
              Cambiar PIN de {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Nuevo PIN (4 dígitos)</Label>
              <Input
                type="text"
                maxLength={4}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
                className="bg-slate-900 border-slate-700 text-white mt-1 text-center text-2xl tracking-widest"
                placeholder="••••"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPinModal(false); setNewPin(""); }} className="border-slate-600 text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleChangePin} disabled={changingPin || newPin.length !== 4} className="bg-yellow-600 hover:bg-yellow-700">
              {changingPin ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cambiar PIN"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-slate-800">
                    <TableHead className="text-slate-400">Usuario</TableHead>
                    <TableHead className="text-slate-400">Categoría</TableHead>
                    <TableHead className="text-slate-400">Email</TableHead>
                    <TableHead className="text-slate-400">Estado</TableHead>
                    <TableHead className="text-slate-400">PIN</TableHead>
                    <TableHead className="text-slate-400 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.user_id} className="border-slate-700 hover:bg-slate-700/50">
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={user.picture} />
                            <AvatarFallback className="bg-red-600 text-white text-xs">
                              {user.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <span className="text-white block">{user.name}</span>
                            <span className="text-slate-500 text-xs">@{user.nickname}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={user.user_category || "personal"}
                          onValueChange={(value) => handleChangeCategory(user.user_id, value)}
                        >
                          <SelectTrigger className="w-32 h-8 bg-red-600 hover:bg-red-700 border-red-600 text-white text-xs font-medium">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value="personal" className="text-white text-xs hover:bg-slate-700">Usuario</SelectItem>
                            <SelectItem value="business" className="text-white text-xs hover:bg-slate-700">Negocio</SelectItem>
                            <SelectItem value="professional" className="text-white text-xs hover:bg-slate-700">Profesional</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">{user.email}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          user.status === 'online' 
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : user.is_banned
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-slate-600/50 text-slate-400'
                        }`}>
                          {user.is_banned ? 'Baneado' : user.status === 'online' ? 'En línea' : 'Offline'}
                        </span>
                        {user.needs_pin_setup && (
                          <span className="ml-2 px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400">
                            PIN temporal
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 h-7 px-2"
                            onClick={() => {
                              setSelectedUser(user);
                              setShowPinModal(true);
                            }}
                          >
                            <Key className="w-3 h-3 mr-1" />
                            Cambiar
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/20 h-7 px-2"
                            onClick={() => handleResetPin(user.user_id, user.name)}
                          >
                            Reset
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end space-x-1">
                          {user.is_banned ? (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20 h-8"
                              onClick={() => handleUnbanUser(user.user_id)}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Desbanear
                            </Button>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/20 h-8 w-8"
                              onClick={() => handleBanUser(user.user_id)}
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-slate-800 border-slate-700">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">¿Eliminar usuario?</AlertDialogTitle>
                                <AlertDialogDescription className="text-slate-400">
                                  Esta acción no se puede deshacer. El usuario {user.name} y todos sus datos serán eliminados permanentemente.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-slate-700 text-white border-slate-600">
                                  Cancelar
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteUser(user.user_id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between p-4 border-t border-slate-700">
                <p className="text-sm text-slate-400">
                  Página {page} de {totalPages}
                </p>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="border-slate-600 text-slate-300"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="border-slate-600 text-slate-300"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Integrations Tab
const IntegrationsTab = () => {
  const [credentials, setCredentials] = useState({
    bubble_api_key: "",
    bubble_base_url: "",
    sendpulse_api_id: "",
    sendpulse_api_secret: "",
    stripe_secret_key: "",
    stripe_public_key: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingBubble, setTestingBubble] = useState(false);
  const [testingSendPulse, setTestingSendPulse] = useState(false);
  const [testingStripe, setTestingStripe] = useState(false);
  const [bubbleStatus, setBubbleStatus] = useState(null);
  const [sendPulseStatus, setSendPulseStatus] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);

  useEffect(() => {
    const fetchCredentials = async () => {
      try {
        const response = await fetch(`${API}/admin/credentials`, {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          setCredentials(data);
        }
      } catch (error) {
        console.error("Error fetching credentials:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCredentials();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/credentials`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(credentials),
      });

      if (response.ok) {
        toast.success("Credenciales guardadas correctamente");
      } else {
        toast.error("Error al guardar credenciales");
      }
    } catch (error) {
      toast.error("Error al guardar credenciales");
    } finally {
      setSaving(false);
    }
  };

  const testBubble = async () => {
    setTestingBubble(true);
    setBubbleStatus(null);
    try {
      const response = await fetch(`${API}/admin/test-bubble`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getAdminHeaders()
        },
      });
      const data = await response.json();
      setBubbleStatus(data);
      if (data.status === "success") {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      setBubbleStatus({ status: "error", message: "Error de conexión" });
      toast.error("Error al probar conexión");
    } finally {
      setTestingBubble(false);
    }
  };

  const testSendPulse = async () => {
    setTestingSendPulse(true);
    setSendPulseStatus(null);
    try {
      const token = localStorage.getItem('admin_session_token');
      console.log('Testing SendPulse with token:', token);
      
      const response = await fetch(`${API}/admin/test-sendpulse`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Bearer ${token}` : ""
        },
      });
      
      console.log('SendPulse test response status:', response.status);
      const data = await response.json();
      console.log('SendPulse test response data:', data);
      
      setSendPulseStatus(data);
      if (data.status === "success") {
        toast.success(data.message);
      } else {
        toast.error(data.message || "Error en la conexión");
      }
    } catch (error) {
      console.error('SendPulse test error:', error);
      setSendPulseStatus({ status: "error", message: `Error de conexión: ${error.message}` });
      toast.error("Error al probar conexión");
    } finally {
      setTestingSendPulse(false);
    }
  };

  const testStripe = async () => {
    setTestingStripe(true);
    setStripeStatus(null);
    try {
      const token = localStorage.getItem('admin_session_token');
      console.log('Testing Stripe with token:', token);
      
      const response = await fetch(`${API}/admin/test-stripe`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": token ? `Bearer ${token}` : ""
        },
      });
      
      console.log('Stripe test response status:', response.status);
      const data = await response.json();
      console.log('Stripe test response data:', data);
      
      setStripeStatus(data);
      if (data.status === "success") {
        toast.success(`${data.message} - Cuenta: ${data.account_id}`);
      } else {
        toast.error(data.message || "Error en la conexión");
      }
    } catch (error) {
      console.error('Stripe test error:', error);
      setStripeStatus({ status: "error", message: `Error de conexión: ${error.message}` });
      toast.error("Error al probar conexión");
    } finally {
      setTestingStripe(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-white mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Configuración de Integraciones
      </h1>

      <div className="grid gap-6">
        {/* Bubble.io Integration */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <div>
                  <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Bubble.io
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Integración para gestión de usuarios
                  </CardDescription>
                </div>
              </div>
              {bubbleStatus && (
                <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
                  bubbleStatus.status === "success" ? "bg-emerald-500/20" : "bg-red-500/20"
                }`}>
                  {bubbleStatus.status === "success" ? (
                    <Check className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <X className="w-4 h-4 text-red-400" />
                  )}
                  <span className={`text-sm ${
                    bubbleStatus.status === "success" ? "text-emerald-400" : "text-red-400"
                  }`}>
                    {bubbleStatus.status === "success" ? "Conectado" : "Error"}
                  </span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="bubble_base_url" className="text-slate-200">
                  URL Base de la App
                </Label>
                <Input
                  id="bubble_base_url"
                  placeholder="https://tu-app.bubbleapps.io"
                  value={credentials.bubble_base_url}
                  onChange={(e) => setCredentials(prev => ({ ...prev, bubble_base_url: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="bubble-base-url-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bubble_api_key" className="text-slate-200">
                  API Key
                </Label>
                <Input
                  id="bubble_api_key"
                  type="password"
                  placeholder="Tu API Key de Bubble.io"
                  value={credentials.bubble_api_key}
                  onChange={(e) => setCredentials(prev => ({ ...prev, bubble_api_key: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="bubble-api-key-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <a
                href="https://manual.bubble.io/help-guides/integrations/api"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-red-400 hover:text-red-300 flex items-center"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Ver documentación
              </a>
              <Button
                onClick={testBubble}
                disabled={testingBubble || !credentials.bubble_api_key || !credentials.bubble_base_url}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="test-bubble-btn"
              >
                {testingBubble ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Probar conexión
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* SendPulse Integration */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">SP</span>
                </div>
                <div>
                  <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    SendPulse
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Integración para notificaciones push y email
                  </CardDescription>
                </div>
              </div>
              {sendPulseStatus && (
                <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${
                  sendPulseStatus.status === "success" ? "bg-emerald-500/20" : "bg-red-500/20"
                }`}>
                  {sendPulseStatus.status === "success" ? (
                    <Check className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <X className="w-4 h-4 text-red-400" />
                  )}
                  <span className={`text-sm ${
                    sendPulseStatus.status === "success" ? "text-emerald-400" : "text-red-400"
                  }`}>
                    {sendPulseStatus.status === "success" ? "Conectado" : "Error"}
                  </span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="sendpulse_api_id" className="text-slate-200">
                  API User ID
                </Label>
                <Input
                  id="sendpulse_api_id"
                  placeholder="Tu API User ID"
                  value={credentials.sendpulse_api_id}
                  onChange={(e) => setCredentials(prev => ({ ...prev, sendpulse_api_id: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="sendpulse-api-id-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sendpulse_api_secret" className="text-slate-200">
                  API Secret
                </Label>
                <Input
                  id="sendpulse_api_secret"
                  type="password"
                  placeholder="Tu API Secret"
                  value={credentials.sendpulse_api_secret}
                  onChange={(e) => setCredentials(prev => ({ ...prev, sendpulse_api_secret: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="sendpulse-api-secret-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <a
                href="https://sendpulse.com/integrations/api"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-red-400 hover:text-red-300 flex items-center"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Ver documentación
              </a>
              <Button
                onClick={testSendPulse}
                disabled={testingSendPulse || !credentials.sendpulse_api_id || !credentials.sendpulse_api_secret}
                variant="outline"
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="test-sendpulse-btn"
              >
                {testingSendPulse ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Probar conexión
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stripe Integration */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">$</span>
              </div>
              <div>
                <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Stripe
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Pagos entre usuarios de la plataforma
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="stripe_secret_key" className="text-slate-200">
                Clave Secreta (Secret Key)
              </Label>
              <Input
                id="stripe_secret_key"
                type="password"
                placeholder="sk_live_... o sk_test_..."
                value={credentials.stripe_secret_key}
                onChange={(e) => setCredentials(prev => ({ ...prev, stripe_secret_key: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="stripe-secret-key-input"
              />
              <p className="text-xs text-slate-500">
                Usa sk_test_... para pruebas o sk_live_... para producción
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stripe_public_key" className="text-slate-200">
                Clave Pública (Publishable Key)
              </Label>
              <Input
                id="stripe_public_key"
                type="text"
                placeholder="pk_live_... o pk_test_..."
                value={credentials.stripe_public_key}
                onChange={(e) => setCredentials(prev => ({ ...prev, stripe_public_key: e.target.value }))}
                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="stripe-public-key-input"
              />
              <p className="text-xs text-slate-500">
                Esta clave se usa en el frontend para procesar pagos
              </p>
            </div>
            {stripeStatus && (
              <div className={`p-3 rounded-lg text-sm ${
                stripeStatus.status === "success" 
                  ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" 
                  : "bg-red-500/10 text-red-400 border border-red-500/30"
              }`}>
                {stripeStatus.message}
                {stripeStatus.account_id && (
                  <span className="block mt-1 text-xs opacity-75">
                    ID de cuenta: {stripeStatus.account_id} ({stripeStatus.country})
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center justify-between pt-2">
              <a
                href="https://dashboard.stripe.com/apikeys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-red-400 hover:text-red-300 flex items-center"
              >
                <ExternalLink className="w-4 h-4 mr-1" />
                Obtener API Keys
              </a>
              <Button
                variant="outline"
                size="sm"
                onClick={testStripe}
                disabled={testingStripe || !credentials.stripe_secret_key}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="test-stripe-btn"
              >
                {testingStripe ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Probando...
                  </>
                ) : (
                  "Probar Conexión"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="save-credentials-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Guardar Cambios
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Embed Tab - Widget and Iframe codes
const EmbedTab = () => {
  const [copied, setCopied] = useState("");
  const [embedMode, setEmbedMode] = useState("nickname"); // anonymous, nickname, or support
  const [widgetPosition, setWidgetPosition] = useState("bottom-right");
  const [widgetColor, setWidgetColor] = useState("#e6342a");
  
  // Use the platform domain for embed codes
  const PLATFORM_URL = "https://okchat.okgo.mx";
  
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast.success("Código copiado al portapapeles");
    setTimeout(() => setCopied(""), 2000);
  };

  // Iframe code - embeds the chat directly
  const iframeCode = `<!-- OkChat Embed - Iframe -->
<iframe
  src="${PLATFORM_URL}/widget?mode=${embedMode}"
  width="400"
  height="600"
  frameborder="0"
  style="border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.3);"
  allow="microphone"
></iframe>`;

  // Widget code - floating button that opens chat
  const widgetCode = `<!-- OkChat Widget - Floating Chat Button -->
<script>
(function() {
  // Configuration
  var config = {
    mode: '${embedMode}',
    position: '${widgetPosition}',
    color: '${widgetColor}'
  };
  
  // Create widget button
  var btn = document.createElement('div');
  btn.id = 'okchat-widget-btn';
  btn.innerHTML = '${embedMode === 'support' 
    ? '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path><line x1="12" y1="2" x2="12" y2="12"></line></svg>'
    : '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>'}';
  btn.style.cssText = 'position:fixed;${widgetPosition === 'bottom-right' ? 'bottom:20px;right:20px;' : widgetPosition === 'bottom-left' ? 'bottom:20px;left:20px;' : 'bottom:20px;right:20px;'}width:60px;height:60px;background:${widgetColor};border-radius:50%;display:flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 4px 15px rgba(0,0,0,0.3);z-index:9998;color:white;transition:transform 0.2s;';
  btn.onmouseover = function() { this.style.transform = 'scale(1.1)'; };
  btn.onmouseout = function() { this.style.transform = 'scale(1)'; };
  
  // Create iframe container (hidden by default)
  var container = document.createElement('div');
  container.id = 'okchat-widget-container';
  container.style.cssText = 'position:fixed;${widgetPosition === 'bottom-right' ? 'bottom:90px;right:20px;' : widgetPosition === 'bottom-left' ? 'bottom:90px;left:20px;' : 'bottom:90px;right:20px;'}width:380px;height:550px;z-index:9999;display:none;';
  container.innerHTML = '<iframe src="${PLATFORM_URL}/widget?mode=' + config.mode + '" style="width:100%;height:100%;border:none;border-radius:12px;box-shadow:0 4px 20px rgba(0,0,0,0.3);" allow="microphone"></iframe>';
  
  // Toggle chat on click
  var isOpen = false;
  btn.onclick = function() {
    isOpen = !isOpen;
    container.style.display = isOpen ? 'block' : 'none';
    btn.innerHTML = isOpen ? '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' : '${embedMode === 'support' 
      ? '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path><line x1="12" y1="2" x2="12" y2="12"></line></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>'}';
  };
  
  document.body.appendChild(btn);
  document.body.appendChild(container);
})();
</script>`;

  return (
    <div>
      <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Integración Embed / Widget
      </h1>
      <p className="text-slate-400 mb-8">
        Integra el chat de OkChat en tu sitio web usando iframe o widget flotante
      </p>

      {/* Configuration */}
      <Card className="bg-slate-800 border-slate-700 mb-6">
        <CardHeader>
          <CardTitle className="text-white">Configuración</CardTitle>
          <CardDescription>Personaliza el comportamiento del chat embebido</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Mode */}
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Modo de acceso</label>
              <select
                value={embedMode}
                onChange={(e) => setEmbedMode(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white"
              >
                <option value="nickname">Login con nickname + PIN</option>
                <option value="anonymous">Invitado (anónimo)</option>
                <option value="support">Modo Soporte</option>
              </select>
              <p className="text-xs text-slate-500">
                {embedMode === 'nickname' 
                  ? 'Los usuarios deben ingresar su nickname y PIN para chatear'
                  : embedMode === 'support'
                    ? 'Los visitantes se conectan directamente con administradores para soporte'
                    : 'Los usuarios pueden chatear como invitados sin registro'}
              </p>
            </div>
            
            {/* Position (for widget) */}
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Posición del widget</label>
              <select
                value={widgetPosition}
                onChange={(e) => setWidgetPosition(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg p-3 text-white"
              >
                <option value="bottom-right">Abajo derecha</option>
                <option value="bottom-left">Abajo izquierda</option>
              </select>
            </div>
            
            {/* Color */}
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Color del widget</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={widgetColor}
                  onChange={(e) => setWidgetColor(e.target.value)}
                  className="w-12 h-12 rounded-lg cursor-pointer border-0"
                />
                <Input
                  value={widgetColor}
                  onChange={(e) => setWidgetColor(e.target.value)}
                  className="flex-1 bg-slate-700 border-slate-600 text-white font-mono"
                  placeholder="#e6342a"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Iframe Code */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Code className="w-5 h-5" />
              Código Iframe
            </CardTitle>
            <CardDescription>
              Incrusta el chat directamente en tu página
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <pre className="bg-slate-900 rounded-lg p-4 text-sm text-slate-300 overflow-x-auto max-h-64 overflow-y-auto">
                <code>{iframeCode}</code>
              </pre>
              <Button
                onClick={() => copyToClipboard(iframeCode, 'iframe')}
                size="sm"
                className={`absolute top-2 right-2 ${copied === 'iframe' ? 'bg-emerald-600' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {copied === 'iframe' ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied === 'iframe' ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
            
            <div className="mt-4 p-4 bg-slate-900 rounded-lg">
              <p className="text-sm text-slate-400 mb-2">Vista previa:</p>
              <div className="bg-slate-950 rounded-lg p-4 flex items-center justify-center" style={{ height: '200px' }}>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-600/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <MessageSquare className="w-8 h-8 text-red-400" />
                  </div>
                  <p className="text-slate-400 text-sm">Chat embebido</p>
                  <p className="text-slate-500 text-xs mt-1">400 x 600 px</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Widget Code */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Código Widget Flotante
            </CardTitle>
            <CardDescription>
              Agrega un botón flotante que abre el chat
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <pre className="bg-slate-900 rounded-lg p-4 text-sm text-slate-300 overflow-x-auto max-h-64 overflow-y-auto">
                <code>{widgetCode}</code>
              </pre>
              <Button
                onClick={() => copyToClipboard(widgetCode, 'widget')}
                size="sm"
                className={`absolute top-2 right-2 ${copied === 'widget' ? 'bg-emerald-600' : 'bg-red-600 hover:bg-red-700'}`}
              >
                {copied === 'widget' ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied === 'widget' ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
            
            <div className="mt-4 p-4 bg-slate-900 rounded-lg">
              <p className="text-sm text-slate-400 mb-2">Vista previa del botón:</p>
              <div className="bg-slate-950 rounded-lg p-4 flex items-end justify-end relative" style={{ height: '200px' }}>
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:scale-110 transition-transform"
                  style={{ backgroundColor: widgetColor }}
                >
                  <MessageSquare className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card className="bg-slate-800 border-slate-700 mt-6">
        <CardHeader>
          <CardTitle className="text-white">Instrucciones de instalación</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-white mb-2">Para Iframe:</h4>
              <ol className="list-decimal list-inside text-sm text-slate-400 space-y-1">
                <li>Copia el código del iframe</li>
                <li>Pégalo en el HTML de tu página donde quieras mostrar el chat</li>
                <li>Ajusta el ancho y alto según necesites</li>
              </ol>
            </div>
            <div>
              <h4 className="font-medium text-white mb-2">Para Widget Flotante:</h4>
              <ol className="list-decimal list-inside text-sm text-slate-400 space-y-1">
                <li>Copia el código del widget</li>
                <li>Pégalo antes del cierre del tag <code className="bg-slate-700 px-1 rounded">&lt;/body&gt;</code></li>
                <li>El botón aparecerá automáticamente en la esquina</li>
              </ol>
            </div>
          </div>
          
          <div className="p-4 bg-red-600/10 border border-red-600/30 rounded-lg">
            <p className="text-sm text-red-300">
              <strong>Nota:</strong> El chat embebido muestra directamente la ventana de acceso (login o chat), 
              sin mostrar la landing page principal. Los usuarios podrán iniciar sesión con su nickname y PIN 
              o chatear como invitados según la configuración seleccionada.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// OAuth SSO Tab
const OAuthSSOTab = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    provider_type: "bubble",
    client_id: "",
    client_secret: "",
    authorization_url: "",
    token_url: "",
    userinfo_url: "",
    redirect_uri: "",
    scopes: "openid profile email",
    enabled: true
  });

  // Helper to get admin auth headers
  const getAdminHeaders = () => {
    const token = localStorage.getItem('admin_session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API}/admin/oauth-providers`, {
        credentials: "include",
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setProviders(data);
      }
    } catch (error) {
      console.error("Error fetching OAuth providers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProvider = async () => {
    try {
      const url = editingProvider 
        ? `${API}/admin/oauth-providers/${editingProvider.provider_id}`
        : `${API}/admin/oauth-providers`;
      
      const response = await fetch(url, {
        method: editingProvider ? "PUT" : "POST",
        headers: { 
          "Content-Type": "application/json",
          ...getAdminHeaders()
        },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingProvider ? "Proveedor actualizado" : "Proveedor creado");
        fetchProviders();
        setShowAddProvider(false);
        setEditingProvider(null);
        resetForm();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al guardar");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const handleDeleteProvider = async (providerId) => {
    try {
      const response = await fetch(`${API}/admin/oauth-providers/${providerId}`, {
        method: "DELETE",
        credentials: "include",
        headers: getAdminHeaders(),
      });

      if (response.ok) {
        toast.success("Proveedor eliminado");
        fetchProviders();
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleToggleProvider = async (provider) => {
    try {
      const response = await fetch(`${API}/admin/oauth-providers/${provider.provider_id}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          ...getAdminHeaders()
        },
        credentials: "include",
        body: JSON.stringify({ ...provider, enabled: !provider.enabled }),
      });

      if (response.ok) {
        toast.success(provider.enabled ? "Proveedor desactivado" : "Proveedor activado");
        fetchProviders();
      }
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      provider_type: "bubble",
      client_id: "",
      client_secret: "",
      authorization_url: "",
      token_url: "",
      userinfo_url: "",
      redirect_uri: `${window.location.origin}/auth/callback`,
      scopes: "openid profile email",
      enabled: true
    });
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setFormData({
      name: provider.name,
      provider_type: provider.provider_type,
      client_id: provider.client_id,
      client_secret: provider.client_secret || "",
      authorization_url: provider.authorization_url,
      token_url: provider.token_url,
      userinfo_url: provider.userinfo_url,
      redirect_uri: provider.redirect_uri,
      scopes: provider.scopes,
      enabled: provider.enabled
    });
    setShowAddProvider(true);
  };

  const applyBubbleTemplate = () => {
    setFormData(prev => ({
      ...prev,
      provider_type: "bubble",
      authorization_url: "https://[TU-APP].bubbleapps.io/api/1.1/oauth/authorize",
      token_url: "https://[TU-APP].bubbleapps.io/api/1.1/oauth/access_token",
      userinfo_url: "https://[TU-APP].bubbleapps.io/api/1.1/obj/user",
      scopes: "openid profile email"
    }));
    toast.info("Plantilla de Bubble.io aplicada. Reemplaza [TU-APP] con el nombre de tu app.");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            OAuth SSO
          </h1>
          <p className="text-slate-400 mt-1">
            Configura proveedores de Single Sign-On para autenticar usuarios
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setEditingProvider(null);
            setShowAddProvider(true);
          }}
          className="bg-red-600 hover:bg-red-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar Proveedor
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-blue-600/30 mb-6">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
            <div>
              <h3 className="font-medium text-white mb-1">¿Qué es OAuth SSO?</h3>
              <p className="text-sm text-slate-300">
                Single Sign-On (SSO) permite que tus usuarios inicien sesión en OkChat usando sus credenciales 
                de otra plataforma (como Bubble.io). Esto simplifica el acceso y mejora la experiencia del usuario.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Providers List */}
      <Card className="bg-slate-800 border-slate-700 mb-6">
        <CardHeader>
          <CardTitle className="text-white">Proveedores Configurados</CardTitle>
        </CardHeader>
        <CardContent>
          {providers.length === 0 ? (
            <div className="text-center py-8">
              <KeyRound className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No hay proveedores configurados</p>
              <p className="text-sm text-slate-500 mt-1">
                Agrega tu primer proveedor OAuth para habilitar SSO
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {providers.map((provider) => (
                <div
                  key={provider.provider_id}
                  className="flex items-center justify-between p-4 bg-slate-900 rounded-lg border border-slate-700"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      provider.provider_type === 'bubble' ? 'bg-blue-600' : 'bg-slate-600'
                    }`}>
                      <Globe className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{provider.name}</p>
                      <p className="text-sm text-slate-400 capitalize">{provider.provider_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      provider.enabled 
                        ? 'bg-emerald-600/20 text-emerald-400' 
                        : 'bg-slate-600/20 text-slate-400'
                    }`}>
                      {provider.enabled ? 'Activo' : 'Inactivo'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleProvider(provider)}
                      className="text-slate-400 hover:text-white"
                    >
                      {provider.enabled ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(provider)}
                      className="text-slate-400 hover:text-white"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-slate-800 border-slate-700">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">¿Eliminar proveedor?</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            Esta acción eliminará permanentemente el proveedor "{provider.name}".
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-slate-700 text-white border-slate-600">
                            Cancelar
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeleteProvider(provider.provider_id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Provider Form */}
      {showAddProvider && (
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white">
              {editingProvider ? 'Editar Proveedor' : 'Nuevo Proveedor OAuth'}
            </CardTitle>
            <CardDescription className="text-slate-400">
              Configura los datos de tu proveedor de autenticación
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Quick Templates */}
            <div className="flex gap-2 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={applyBubbleTemplate}
                className="border-blue-600 text-blue-400 hover:bg-blue-600/10"
              >
                <Globe className="w-4 h-4 mr-2" />
                Usar plantilla Bubble.io
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Nombre del proveedor</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Mi App Bubble"
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Tipo de proveedor</Label>
                <select
                  value={formData.provider_type}
                  onChange={(e) => setFormData({...formData, provider_type: e.target.value})}
                  className="w-full h-10 bg-slate-900 border border-slate-700 text-white rounded-md px-3 mt-1"
                >
                  <option value="bubble">Bubble.io</option>
                  <option value="custom">Custom OAuth 2.0</option>
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Client ID</Label>
                <Input
                  value={formData.client_id}
                  onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                  placeholder="Tu Client ID de OAuth"
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Client Secret</Label>
                <Input
                  type="password"
                  value={formData.client_secret}
                  onChange={(e) => setFormData({...formData, client_secret: e.target.value})}
                  placeholder="Tu Client Secret"
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Authorization URL</Label>
              <Input
                value={formData.authorization_url}
                onChange={(e) => setFormData({...formData, authorization_url: e.target.value})}
                placeholder="https://tu-app.bubbleapps.io/api/1.1/oauth/authorize"
                className="bg-slate-900 border-slate-700 text-white mt-1"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Token URL</Label>
                <Input
                  value={formData.token_url}
                  onChange={(e) => setFormData({...formData, token_url: e.target.value})}
                  placeholder="https://tu-app.bubbleapps.io/api/1.1/oauth/access_token"
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">User Info URL</Label>
                <Input
                  value={formData.userinfo_url}
                  onChange={(e) => setFormData({...formData, userinfo_url: e.target.value})}
                  placeholder="https://tu-app.bubbleapps.io/api/1.1/obj/user"
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Redirect URI (Callback URL)</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={formData.redirect_uri}
                  onChange={(e) => setFormData({...formData, redirect_uri: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(formData.redirect_uri)}
                  className="border-slate-700 text-slate-300"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Usa esta URL como Redirect URI en la configuración OAuth de Bubble.io
              </p>
            </div>

            <div>
              <Label className="text-slate-300">Scopes</Label>
              <Input
                value={formData.scopes}
                onChange={(e) => setFormData({...formData, scopes: e.target.value})}
                placeholder="openid profile email"
                className="bg-slate-900 border-slate-700 text-white mt-1"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSaveProvider}
                className="bg-red-600 hover:bg-red-700"
              >
                <Check className="w-4 h-4 mr-2" />
                {editingProvider ? 'Guardar Cambios' : 'Crear Proveedor'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddProvider(false);
                  setEditingProvider(null);
                  resetForm();
                }}
                className="border-slate-600 text-slate-300"
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bubble.io Instructions */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-white">Configuración en Bubble.io</CardTitle>
              <CardDescription className="text-slate-400">
                Guía paso a paso para configurar OAuth en tu app de Bubble
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold shrink-0">
                1
              </div>
              <div>
                <h4 className="font-medium text-white">Habilita OAuth en tu app de Bubble</h4>
                <p className="text-sm text-slate-400 mt-1">
                  Ve a <span className="text-blue-400">Settings → API</span> en tu editor de Bubble. 
                  Activa <span className="text-white">"Enable OAuth2 as a login method"</span>.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold shrink-0">
                2
              </div>
              <div>
                <h4 className="font-medium text-white">Obtén tu Client ID y Client Secret</h4>
                <p className="text-sm text-slate-400 mt-1">
                  En la misma sección de API, encontrarás tu <span className="text-white">Client ID</span> y 
                  podrás generar un <span className="text-white">Client Secret</span>. Guárdalos de forma segura.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold shrink-0">
                3
              </div>
              <div>
                <h4 className="font-medium text-white">Configura la Redirect URI</h4>
                <p className="text-sm text-slate-400 mt-1">
                  En Bubble, agrega la siguiente URL como <span className="text-white">Authorized redirect URI</span>:
                </p>
                <div className="flex items-center gap-2 mt-2 p-2 bg-slate-900 rounded border border-slate-700">
                  <code className="text-sm text-emerald-400 flex-1">
                    {window.location.origin}/auth/oauth/callback
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(`${window.location.origin}/auth/oauth/callback`)}
                    className="text-slate-400 hover:text-white"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold shrink-0">
                4
              </div>
              <div>
                <h4 className="font-medium text-white">Crea el proveedor en OkChat</h4>
                <p className="text-sm text-slate-400 mt-1">
                  Usa el botón "Agregar Proveedor" arriba y selecciona la plantilla de Bubble.io. 
                  Reemplaza <span className="text-white">[TU-APP]</span> con el nombre de tu aplicación de Bubble.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold shrink-0">
                5
              </div>
              <div>
                <h4 className="font-medium text-white">Expone los datos del usuario</h4>
                <p className="text-sm text-slate-400 mt-1">
                  En Bubble, ve a <span className="text-blue-400">Data → Privacy</span> y asegúrate de que 
                  los campos del usuario (email, nombre) sean accesibles via API para usuarios autenticados.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-amber-600/10 border border-amber-600/30 rounded-lg">
            <div className="flex gap-2">
              <Info className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-200 font-medium">URLs típicas de Bubble.io</p>
                <ul className="text-sm text-amber-100/80 mt-2 space-y-1">
                  <li>• Authorization: <code className="text-xs bg-amber-600/20 px-1 rounded">https://[app].bubbleapps.io/api/1.1/oauth/authorize</code></li>
                  <li>• Token: <code className="text-xs bg-amber-600/20 px-1 rounded">https://[app].bubbleapps.io/api/1.1/oauth/access_token</code></li>
                  <li>• User Info: <code className="text-xs bg-amber-600/20 px-1 rounded">https://[app].bubbleapps.io/api/1.1/obj/user</code></li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// API Connection Tab - Complete API Documentation and Webhook Management
const APIConnectionTab = ({ getAdminHeaders }) => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddWebhook, setShowAddWebhook] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    name: "",
    url: "",
    events: [],
    secret: ""
  });
  const [saving, setSaving] = useState(false);
  const [copiedEndpoint, setCopiedEndpoint] = useState(null);

  const availableEvents = [
    { id: "message.sent", label: "Mensaje Enviado", description: "Cuando un usuario envía un mensaje" },
    { id: "message.received", label: "Mensaje Recibido", description: "Cuando se recibe un mensaje" },
    { id: "user.created", label: "Usuario Creado", description: "Cuando se crea un nuevo usuario" },
    { id: "user.updated", label: "Usuario Actualizado", description: "Cuando se actualiza un usuario" },
    { id: "user.deleted", label: "Usuario Eliminado", description: "Cuando se elimina un usuario" },
    { id: "conversation.created", label: "Conversación Creada", description: "Cuando se inicia una nueva conversación" },
    { id: "walkie.message", label: "Mensaje Walkie-Talkie", description: "Cuando se envía un mensaje de voz" },
  ];

  const apiEndpoints = [
    {
      category: "Autenticación",
      endpoints: [
        { method: "POST", path: "/api/auth/nickname", description: "Login con nickname y PIN", params: "nickname, pin" },
        { method: "GET", path: "/api/auth/me", description: "Obtener usuario actual", params: "Bearer Token" },
        { method: "GET", path: "/api/auth/check-nickname/{nickname}", description: "Verificar si existe un nickname", params: "nickname" },
        { method: "PUT", path: "/api/user/pin", description: "Actualizar PIN del usuario", params: "pin" },
      ]
    },
    {
      category: "Usuarios",
      endpoints: [
        { method: "GET", path: "/api/admin/users", description: "Listar todos los usuarios", params: "page, limit" },
        { method: "POST", path: "/api/admin/users", description: "Crear nuevo usuario", params: "name, nickname, email, user_category" },
        { method: "DELETE", path: "/api/admin/users/{user_id}", description: "Eliminar usuario", params: "user_id" },
        { method: "PUT", path: "/api/admin/users/{user_id}/pin", description: "Cambiar PIN de usuario", params: "new_pin" },
        { method: "PUT", path: "/api/admin/users/{user_id}/category", description: "Cambiar categoría de usuario", params: "category" },
        { method: "POST", path: "/api/admin/users/{user_id}/reset-pin", description: "Resetear PIN a 9999", params: "user_id" },
        { method: "POST", path: "/api/admin/users/{user_id}/ban", description: "Banear usuario", params: "reason" },
        { method: "POST", path: "/api/admin/users/{user_id}/unban", description: "Desbanear usuario", params: "user_id" },
        { method: "GET", path: "/api/user/profile", description: "Obtener perfil del usuario actual", params: "Bearer Token" },
      ]
    },
    {
      category: "Mensajes y Conversaciones",
      endpoints: [
        { method: "GET", path: "/api/conversations", description: "Listar conversaciones del usuario", params: "Bearer Token" },
        { method: "POST", path: "/api/conversations", description: "Crear nueva conversación", params: "participant_id" },
        { method: "GET", path: "/api/conversations/{id}/messages", description: "Obtener mensajes de conversación", params: "conversation_id, page, limit" },
        { method: "POST", path: "/api/messages", description: "Enviar mensaje de texto", params: "conversation_id, content, type" },
        { method: "POST", path: "/api/messages/voice", description: "Enviar mensaje de voz", params: "conversation_id, audio_file" },
        { method: "POST", path: "/api/messages/temporary", description: "Enviar mensaje temporal", params: "conversation_id, content, expires_in" },
        { method: "DELETE", path: "/api/messages/{message_id}", description: "Eliminar mensaje", params: "message_id" },
      ]
    },
    {
      category: "Archivos y Media",
      endpoints: [
        { method: "POST", path: "/api/upload/image", description: "Subir imagen", params: "file, conversation_id" },
        { method: "POST", path: "/api/upload/video", description: "Subir video", params: "file, conversation_id" },
        { method: "POST", path: "/api/upload/document", description: "Subir documento", params: "file, conversation_id" },
        { method: "POST", path: "/api/upload/audio", description: "Subir audio", params: "file, conversation_id" },
      ]
    },
    {
      category: "Walkie-Talkie",
      endpoints: [
        { method: "POST", path: "/api/walkie/send", description: "Enviar mensaje walkie-talkie", params: "conversation_id, audio_blob" },
        { method: "GET", path: "/api/walkie/history/{conversation_id}", description: "Historial de walkie-talkie", params: "conversation_id" },
      ]
    },
    {
      category: "Administración",
      endpoints: [
        { method: "POST", path: "/api/admin/login", description: "Login de administrador", params: "email, password" },
        { method: "GET", path: "/api/admin/stats", description: "Estadísticas del sistema", params: "Bearer Admin Token" },
        { method: "GET", path: "/api/admin/me", description: "Información del admin actual", params: "Bearer Admin Token" },
      ]
    },
    {
      category: "Webhooks",
      endpoints: [
        { method: "GET", path: "/api/webhooks", description: "Listar webhooks configurados", params: "Bearer Admin Token" },
        { method: "POST", path: "/api/webhooks", description: "Crear nuevo webhook", params: "name, url, events, secret" },
        { method: "DELETE", path: "/api/webhooks/{webhook_id}", description: "Eliminar webhook", params: "webhook_id" },
        { method: "POST", path: "/api/webhooks/{webhook_id}/test", description: "Probar webhook", params: "webhook_id" },
      ]
    },
  ];

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      const response = await fetch(`${API}/admin/webhooks`, {
        credentials: "include",
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setWebhooks(data || []);
      }
    } catch (error) {
      console.error("Error fetching webhooks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebhook = async () => {
    if (!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0) {
      toast.error("Completa todos los campos requeridos");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/webhooks`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          ...getAdminHeaders()
        },
        credentials: "include",
        body: JSON.stringify(newWebhook),
      });

      if (response.ok) {
        toast.success("Webhook creado exitosamente");
        setShowAddWebhook(false);
        setNewWebhook({ name: "", url: "", events: [], secret: "" });
        fetchWebhooks();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al crear webhook");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    try {
      const response = await fetch(`${API}/admin/webhooks/${webhookId}`, {
        method: "DELETE",
        credentials: "include",
        headers: getAdminHeaders(),
      });

      if (response.ok) {
        toast.success("Webhook eliminado");
        fetchWebhooks();
      } else {
        toast.error("Error al eliminar webhook");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const handleTestWebhook = async (webhookId) => {
    try {
      const response = await fetch(`${API}/admin/webhooks/${webhookId}/test`, {
        method: "POST",
        credentials: "include",
        headers: getAdminHeaders(),
      });

      if (response.ok) {
        toast.success("Webhook probado exitosamente");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al probar webhook");
      }
    } catch (error) {
      toast.error("Error de conexión");
    }
  };

  const copyToClipboard = (text, endpointId) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(endpointId);
    setTimeout(() => setCopiedEndpoint(null), 2000);
    toast.success("Copiado al portapapeles");
  };

  const toggleEvent = (eventId) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(eventId)
        ? prev.events.filter(e => e !== eventId)
        : [...prev.events, eventId]
    }));
  };

  const getMethodColor = (method) => {
    switch (method) {
      case "GET": return "bg-green-600/20 text-green-400";
      case "POST": return "bg-blue-600/20 text-blue-400";
      case "PUT": return "bg-yellow-600/20 text-yellow-400";
      case "DELETE": return "bg-red-600/20 text-red-400";
      default: return "bg-slate-600/20 text-slate-400";
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Conexión API
          </h1>
          <p className="text-slate-400 mt-1">Documentación completa, API Keys y gestión de webhooks</p>
        </div>
      </div>

      {/* API Keys Management */}
      <APIKeysManager getAdminHeaders={getAdminHeaders} />

      {/* API Documentation */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Code className="w-5 h-5 text-blue-400" />
            Documentación de API
          </CardTitle>
          <CardDescription className="text-slate-400">
            Todos los endpoints disponibles para integrar OkChat con tu aplicación
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-300">
              <strong>URL Base:</strong> <code className="bg-blue-600/20 px-2 py-0.5 rounded">{window.location.origin}/api</code>
            </p>
            <p className="text-sm text-blue-300 mt-2">
              <strong>Autenticación:</strong> Bearer Token en header <code className="bg-blue-600/20 px-2 py-0.5 rounded">Authorization: Bearer {"{token}"}</code>
            </p>
          </div>

          <Tabs defaultValue="auth" className="w-full">
            <TabsList className="bg-slate-700/50 flex-wrap h-auto gap-1 p-1">
              {apiEndpoints.map((cat) => (
                <TabsTrigger
                  key={cat.category}
                  value={cat.category.toLowerCase().replace(/\s/g, '-')}
                  className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs px-2 py-1"
                >
                  {cat.category}
                </TabsTrigger>
              ))}
            </TabsList>

            {apiEndpoints.map((cat) => (
              <TabsContent key={cat.category} value={cat.category.toLowerCase().replace(/\s/g, '-')} className="mt-4">
                <div className="space-y-2">
                  {cat.endpoints.map((endpoint, idx) => (
                    <div key={idx} className="p-3 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-mono font-bold ${getMethodColor(endpoint.method)}`}>
                            {endpoint.method}
                          </span>
                          <code className="text-sm text-white font-mono">{endpoint.path}</code>
                        </div>
                        <button
                          onClick={() => copyToClipboard(endpoint.path, `${cat.category}-${idx}`)}
                          className="p-1 text-slate-400 hover:text-white transition-colors"
                        >
                          {copiedEndpoint === `${cat.category}-${idx}` ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <p className="text-sm text-slate-400 mt-2">{endpoint.description}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        <span className="text-slate-400">Parámetros:</span> {endpoint.params}
                      </p>
                    </div>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Webhooks Management */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Webhook className="w-5 h-5 text-purple-400" />
                Gestión de Webhooks
              </CardTitle>
              <CardDescription className="text-slate-400">
                Configura webhooks para recibir notificaciones en tiempo real
              </CardDescription>
            </div>
            <Button 
              onClick={() => setShowAddWebhook(true)}
              className="bg-red-600 hover:bg-red-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Agregar Webhook
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
          ) : webhooks.length === 0 ? (
            <div className="text-center py-8">
              <Webhook className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No hay webhooks configurados</p>
              <p className="text-sm text-slate-500 mt-1">Crea un webhook para recibir notificaciones de eventos</p>
            </div>
          ) : (
            <div className="space-y-3">
              {webhooks.map((webhook) => (
                <div key={webhook.id} className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-white">{webhook.name}</h4>
                      <p className="text-sm text-slate-400 font-mono mt-1">{webhook.url}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {webhook.events?.map((event) => (
                          <span key={event} className="px-2 py-0.5 bg-purple-600/20 text-purple-400 text-xs rounded">
                            {event}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-400 hover:text-blue-300"
                        onClick={() => handleTestWebhook(webhook.id)}
                      >
                        <Activity className="w-4 h-4 mr-1" />
                        Probar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300"
                        onClick={() => handleDeleteWebhook(webhook.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Webhook Dialog */}
      <Dialog open={showAddWebhook} onOpenChange={setShowAddWebhook}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Webhook className="w-5 h-5 text-purple-400" />
              Agregar Webhook
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Configura un nuevo webhook para recibir notificaciones de eventos
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Nombre del Webhook</Label>
              <Input
                value={newWebhook.name}
                onChange={(e) => setNewWebhook({...newWebhook, name: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white mt-1"
                placeholder="Mi Webhook"
              />
            </div>

            <div>
              <Label className="text-slate-300">URL del Endpoint</Label>
              <Input
                value={newWebhook.url}
                onChange={(e) => setNewWebhook({...newWebhook, url: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white mt-1"
                placeholder="https://tu-servidor.com/webhook"
              />
            </div>

            <div>
              <Label className="text-slate-300">Secret (opcional)</Label>
              <Input
                value={newWebhook.secret}
                onChange={(e) => setNewWebhook({...newWebhook, secret: e.target.value})}
                className="bg-slate-900 border-slate-700 text-white mt-1"
                placeholder="Tu secreto para verificar firmas"
              />
              <p className="text-xs text-slate-500 mt-1">Se enviará en el header X-Webhook-Secret</p>
            </div>

            <div>
              <Label className="text-slate-300 mb-2 block">Eventos a escuchar</Label>
              <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                {availableEvents.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => toggleEvent(event.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      newWebhook.events.includes(event.id)
                        ? 'bg-purple-600/20 border-purple-500'
                        : 'bg-slate-900/50 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-white">{event.label}</p>
                        <p className="text-xs text-slate-400">{event.description}</p>
                      </div>
                      {newWebhook.events.includes(event.id) && (
                        <Check className="w-4 h-4 text-purple-400" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddWebhook(false)} className="border-slate-600 text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleCreateWebhook} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear Webhook"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Example Webhook Payload */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Code className="w-5 h-5 text-green-400" />
            Ejemplo de Payload
          </CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="bg-slate-900 p-4 rounded-lg overflow-x-auto text-sm">
            <code className="text-green-400">{`{
  "event": "message.sent",
  "timestamp": "2026-04-01T12:00:00Z",
  "data": {
    "message_id": "msg_abc123",
    "conversation_id": "conv_xyz789",
    "sender": {
      "user_id": "user_123",
      "nickname": "john_doe",
      "name": "John Doe"
    },
    "content": "Hola, ¿cómo estás?",
    "type": "text",
    "created_at": "2026-04-01T12:00:00Z"
  }
}`}</code>
          </pre>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
